import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import HistoryIcon from "@mui/icons-material/History";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { KEYBOARD_SHORTCUTS } from "../constants/globalSearch.constants";

interface EmptyStateProps {
  type: "initial" | "recent" | "no-results" | "error";
  query?: string;
  recentSearches?: string[];
  onSelectRecent?: (search: string) => void;
}

export default function GlobalSearchEmptyState({
  type,
  query,
  recentSearches = [],
  onSelectRecent,
}: EmptyStateProps) {
  const isMac =
    typeof navigator !== "undefined" &&
    navigator.platform.toUpperCase().indexOf("MAC") >= 0;
  const shortcutKey = isMac
    ? KEYBOARD_SHORTCUTS.toggle.mac
    : KEYBOARD_SHORTCUTS.toggle.windows;

  if (type === "recent" && recentSearches.length > 0) {
    return (
      <Box sx={{ py: 1 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ px: 2, py: 1, display: "block" }}
        >
          Recent searches
        </Typography>
        <List dense disablePadding>
          {recentSearches.map((search, index) => (
            <ListItemButton
              key={index}
              onClick={() => onSelectRecent?.(search)}
              data-testid={`global-search-recent-${index}`}
              data-attr={`global-search:link:recent-search-${index}`}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <HistoryIcon fontSize="small" color="action" />
              </ListItemIcon>
              <ListItemText primary={search} />
            </ListItemButton>
          ))}
        </List>
      </Box>
    );
  }

  if (type === "no-results") {
    return (
      <Box
        sx={{
          py: 4,
          px: 2,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1,
        }}
      >
        <SearchOffIcon sx={{ fontSize: 48, color: "text.disabled" }} />
        <Typography variant="body1" color="text.secondary">
          No results found for &quot;{query}&quot;
        </Typography>
        <Typography variant="caption" color="text.disabled">
          Try a different search term or switch search mode
        </Typography>
      </Box>
    );
  }

  if (type === "error") {
    return (
      <Box
        sx={{
          py: 4,
          px: 2,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1,
        }}
      >
        <ErrorOutlineIcon sx={{ fontSize: 48, color: "error.main" }} />
        <Typography variant="body1" color="error">
          Search failed
        </Typography>
        <Typography variant="caption" color="text.disabled">
          Please try again later
        </Typography>
      </Box>
    );
  }

  // Initial state
  return (
    <Box
      sx={{
        py: 4,
        px: 2,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 1,
      }}
    >
      <SearchIcon sx={{ fontSize: 48, color: "text.disabled" }} />
      <Typography variant="body1" color="text.secondary">
        Search datasets and documents
      </Typography>
      <Typography variant="caption" color="text.disabled">
        Press {shortcutKey} anytime to open search
      </Typography>
    </Box>
  );
}
